import * as THREE from 'three'
import { addObject3DToParent } from '../utils/scene-object/addObject3DToParent'

/** 实体显示面的生命周期状态。 */
export type ModelBodySurfaceLifecycleState = 'active' | 'preview' | 'inactive'

/** 单个逻辑实体在共享 Mesh 中的显示分区。 */
interface ModelBodySurfacePartition {
  /** 注册键。 */
  representationId: string
  /** 该实体占用的 geometry group 索引；缺失表示独占整个 Mesh。 */
  primitiveIndices: readonly number[] | null
  /** 当前生命周期状态。 */
  state: ModelBodySurfaceLifecycleState
}

/** Mesh 原始场景与渲染状态快照。 */
interface ModelBodySurfaceSnapshot {
  /** 原父节点。 */
  parent: THREE.Object3D | null
  /** 原父节点内索引。 */
  childIndex: number
  /** 原可见性。 */
  visible: boolean
  /** 原几何体。 */
  geometry: THREE.BufferGeometry
  /** 原材质槽。 */
  material: THREE.Material | THREE.Material[]
}

/**
 * 单个显示 Mesh 的实体分区生命周期控制器。
 *
 * 职责与边界：
 * 1. 只处理可见表面的预览抑制、提交摘除与恢复；
 * 2. 多个逻辑实体共享 Mesh 时按 geometry group 过滤，绝不隐藏仍活动的兄弟实体；
 * 3. 不管理拓扑宿主，预览期间因此不会破坏点、线、面代理的拾取生命周期。
 */
export class ModelBodySurfaceState {
  /** 当前 Mesh 上注册的实体分区。 */
  private readonly partitions = new Map<string, ModelBodySurfacePartition>()

  /** 首次注册时冻结的原始状态。 */
  private readonly snapshot: ModelBodySurfaceSnapshot

  /** 当前临时隐藏材质。 */
  private hiddenMaterial: THREE.Material | THREE.Material[] | null = null

  /** 当前按 group 过滤生成的轻量几何包装。 */
  private filteredGeometry: THREE.BufferGeometry | null = null

  /**
   * 创建显示面状态控制器。
   *
   * @param mesh 被一个或多个逻辑实体共享的显示 Mesh。
   */
  public constructor(private readonly mesh: THREE.Mesh) {
    this.snapshot = {
      parent: mesh.parent,
      childIndex: mesh.parent?.children.indexOf(mesh) ?? -1,
      visible: mesh.visible,
      geometry: mesh.geometry,
      material: mesh.material,
    }
  }

  /**
   * 注册或更新一个逻辑实体的显示分区。
   *
   * @param representationId 场景表示注册键。
   * @param primitiveIndices geometry group 索引；独占 Mesh 时可省略。
   */
  public registerPartition(
    representationId: string,
    primitiveIndices?: readonly number[],
  ): void {
    this.captureCurrentActiveState()
    const previousState = this.partitions.get(representationId)?.state ?? 'active'
    this.partitions.set(representationId, {
      representationId,
      primitiveIndices: primitiveIndices && primitiveIndices.length > 0
        ? [...new Set(primitiveIndices)]
        : null,
      state: previousState,
    })
    this.applyState()
  }

  /**
   * 注销一个实体分区。
   *
   * @param representationId 场景表示注册键。
   */
  public unregisterPartition(representationId: string): void {
    this.captureCurrentActiveState()
    this.partitions.delete(representationId)
    this.applyState()
  }

  /**
   * 更新一个实体分区的生命周期状态。
   *
   * @param representationId 场景表示注册键。
   * @param state 新状态。
   */
  public setPartitionState(
    representationId: string,
    state: ModelBodySurfaceLifecycleState,
  ): void {
    const partition = this.partitions.get(representationId)
    if (!partition || partition.state === state) {
      return
    }
    this.captureCurrentActiveState()
    partition.state = state
    this.applyState()
  }

  /**
   * 恢复原始 Mesh 状态并释放本控制器创建的临时资源。
   */
  public dispose(): void {
    this.captureCurrentActiveState()
    this.restoreRenderResources()
    this.restoreParent()
    this.mesh.visible = this.snapshot.visible
    this.partitions.clear()
  }

  /** 根据全部分区状态原子刷新显示面。 */
  private applyState(): void {
    if (this.partitions.size === 0) {
      this.restoreRenderResources()
      this.restoreParent()
      this.mesh.visible = this.snapshot.visible
      return
    }

    const partitions = [...this.partitions.values()]
    const activePartitions = partitions.filter((partition) => partition.state === 'active')
    if (activePartitions.length === partitions.length) {
      this.restoreRenderResources()
      this.restoreParent()
      this.mesh.visible = this.snapshot.visible
      return
    }

    if (activePartitions.length === 0) {
      const hasPreviewPartition = partitions.some((partition) => partition.state === 'preview')
      if (hasPreviewPartition) {
        this.restoreParent()
        this.applyHiddenMaterial()
        this.mesh.visible = this.snapshot.visible
        return
      }
      this.restoreRenderResources()
      this.mesh.removeFromParent()
      return
    }

    this.restoreParent()
    this.applyFilteredGeometry(activePartitions)
    this.mesh.visible = this.snapshot.visible
  }

  /**
   * 在全部分区仍活动时捕获外部最新渲染状态。
   *
   * 显示模式、材质外观和场景重挂载可能在注册完成后更新 Mesh；预览替换必须从进入
   * 状态机前的真实状态恢复，不能永久回退到首次注册快照。
   */
  private captureCurrentActiveState(): void {
    if ([...this.partitions.values()].some((partition) => partition.state !== 'active')) {
      return
    }
    this.snapshot.parent = this.mesh.parent
    this.snapshot.childIndex = this.mesh.parent?.children.indexOf(this.mesh) ?? -1
    this.snapshot.visible = this.mesh.visible
    this.snapshot.geometry = this.mesh.geometry
    this.snapshot.material = this.mesh.material
  }

  /** 使用隐藏材质抑制整个 Mesh 表面，但保持 Object3D 子树可遍历。 */
  private applyHiddenMaterial(): void {
    this.releaseFilteredGeometry()
    this.mesh.geometry = this.snapshot.geometry
    if (!this.hiddenMaterial) {
      this.hiddenMaterial = Array.isArray(this.snapshot.material)
        ? this.snapshot.material.map((material) => this.cloneHiddenMaterial(material))
        : this.cloneHiddenMaterial(this.snapshot.material)
    }
    this.mesh.material = this.hiddenMaterial
  }

  /**
   * 仅保留活动实体所占 geometry group。
   *
   * @param activePartitions 当前仍活动的实体分区。
   */
  private applyFilteredGeometry(activePartitions: readonly ModelBodySurfacePartition[]): void {
    const allPartitions = [...this.partitions.values()]
    if (allPartitions.some((partition) => partition.primitiveIndices === null)) {
      throw new Error(`共享实体 Mesh 缺少 primitive 分区，无法只替换目标实体: ${this.mesh.uuid}`)
    }
    if (this.snapshot.geometry.groups.length === 0) {
      throw new Error(`共享实体 Mesh 缺少 geometry groups，无法只替换目标实体: ${this.mesh.uuid}`)
    }

    const activeGroupIndexes = new Set(
      activePartitions.flatMap((partition) => partition.primitiveIndices ?? []),
    )
    const filteredGeometry = this.createSharedAttributeGeometry()
    this.snapshot.geometry.groups.forEach((group, groupIndex) => {
      if (activeGroupIndexes.has(groupIndex)) {
        filteredGeometry.addGroup(group.start, group.count, group.materialIndex)
      }
    })
    if (filteredGeometry.groups.length === 0) {
      filteredGeometry.dispose()
      throw new Error(`共享实体 Mesh 的活动 primitive 无可渲染 group: ${this.mesh.uuid}`)
    }

    this.releaseHiddenMaterial()
    this.releaseFilteredGeometry()
    this.filteredGeometry = filteredGeometry
    this.mesh.geometry = filteredGeometry
    this.mesh.material = this.snapshot.material
  }

  /**
   * 创建共享原顶点缓冲、仅独立持有 groups 的轻量几何包装。
   *
   * @returns 可安全修改 groups 的 BufferGeometry。
   */
  private createSharedAttributeGeometry(): THREE.BufferGeometry {
    const source = this.snapshot.geometry
    const geometry = new THREE.BufferGeometry()
    Object.entries(source.attributes).forEach(([name, attribute]) => {
      geometry.setAttribute(name, attribute)
    })
    geometry.setIndex(source.index)
    geometry.morphAttributes = source.morphAttributes
    geometry.morphTargetsRelative = source.morphTargetsRelative
    geometry.boundingBox = source.boundingBox?.clone() ?? null
    geometry.boundingSphere = source.boundingSphere?.clone() ?? null
    geometry.drawRange = { ...source.drawRange }
    geometry.userData = { ...source.userData }
    return geometry
  }

  /**
   * 克隆不会渲染表面的临时材质。
   *
   * @param material 原材质。
   * @returns 隐藏材质克隆。
   */
  private cloneHiddenMaterial(material: THREE.Material): THREE.Material {
    const hiddenMaterial = material.clone()
    hiddenMaterial.visible = false
    hiddenMaterial.needsUpdate = true
    return hiddenMaterial
  }

  /** 恢复原始几何与材质并释放临时资源。 */
  private restoreRenderResources(): void {
    this.mesh.geometry = this.snapshot.geometry
    this.mesh.material = this.snapshot.material
    this.releaseFilteredGeometry()
    this.releaseHiddenMaterial()
  }

  /** 释放临时过滤几何包装。 */
  private releaseFilteredGeometry(): void {
    this.filteredGeometry?.dispose()
    this.filteredGeometry = null
  }

  /** 释放临时隐藏材质。 */
  private releaseHiddenMaterial(): void {
    if (!this.hiddenMaterial) {
      return
    }
    const materials = Array.isArray(this.hiddenMaterial)
      ? this.hiddenMaterial
      : [this.hiddenMaterial]
    materials.forEach((material) => material.dispose())
    this.hiddenMaterial = null
  }

  /** 按原父节点与原顺序恢复 Mesh。 */
  private restoreParent(): void {
    if (!this.snapshot.parent || this.mesh.parent === this.snapshot.parent) {
      return
    }
    addObject3DToParent(
      this.mesh,
      this.snapshot.parent,
      this.snapshot.childIndex >= 0 ? this.snapshot.childIndex : undefined,
    )
  }
}
